import { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Flex,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import { adminApi } from "../../../data/axios/adminApi";
import { UserAccessLog, AdminUser } from "../../../types/admin";
import { formatSp } from "../../../lib/datetime";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUser | null;
}

const formatDate = (iso: string) => formatSp(iso, "dd/MM/yyyy HH:mm");

export function UserAccessLogModal({ isOpen, onClose, user }: Props) {
  const [logs, setLogs] = useState<UserAccessLog[]>([]);
  const [loading, setLoading] = useState(false);

  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const descColor   = useColorModeValue("#696a71", "#8fa3b8");
  const subtleColor = useColorModeValue("#84858c", "#718096");
  const cardBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const itemBg      = useColorModeValue("rgba(0,0,0,0.02)", "rgba(255,255,255,0.03)");

  useEffect(() => {
    if (!isOpen || !user) return;
    setLoading(true);
    adminApi
      .getUserAccessLogs(user.id)
      .then((data) => setLogs(data.logs))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [isOpen, user]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent borderRadius="2xl">
        <ModalHeader
          fontSize="lg"
          fontWeight={600}
          color={titleColor}
          borderBottomWidth="1px"
          borderColor={cardBorder}
          pb={4}
        >
          Histórico de Acesso — {user?.name}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} pt={5}>
          {loading ? (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="md" color="#009efb" />
            </Flex>
          ) : logs.length === 0 ? (
            <Text color={subtleColor} fontSize="sm">Nenhum registro de acesso encontrado.</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {logs.map((log) => (
                <Box
                  key={log.id}
                  p={3}
                  borderRadius="8px"
                  borderWidth="1px"
                  borderColor={cardBorder}
                  bg={itemBg}
                  _hover={{ borderColor: "rgba(0,158,251,0.25)" }}
                  transition="border-color 0.15s"
                >
                  <HStack justify="space-between" mb={log.user_agent ? 1 : 0}>
                    <Badge
                      borderRadius="50px"
                      bg="rgba(0,158,251,0.12)"
                      color="#009efb"
                      fontSize="11px"
                      fontWeight={600}
                      px={2.5}
                      py={0.5}
                    >
                      {formatDate(log.logged_at)}
                    </Badge>
                    {log.ip_address && (
                      <Text fontSize="xs" color={descColor} fontFamily="mono">
                        {log.ip_address}
                      </Text>
                    )}
                  </HStack>
                  {log.user_agent && (
                    <Text fontSize="xs" color={subtleColor} noOfLines={1} mt={0.5}>
                      {log.user_agent}
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
